/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { AzureBlobSwagger } from 'src/core/services/azure/azureSwaggers';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { getStorageUrl } from 'src/core/utils/api/storageUtils';

const ContainerHeaderName = 'x-ms-version';
const ContainerHeaderBlobType = 'x-ms-blob-type';

export const parseBlobXmlResponse = (response: string): { blobs: AzureBlobSwagger[]; marker: string } => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(response, 'text/xml');
    const blobs: AzureBlobSwagger[] = [];

    const xmlBlobs = xmlDoc.getElementsByTagName('Blob');
    for (const xmlBlob of xmlBlobs) {
        const name = xmlBlob.getElementsByTagName('Name')[0].textContent;
        const xmlBlobProps = xmlBlob.getElementsByTagName('Properties')[0];
        const blobType = xmlBlobProps.getElementsByTagName('Content-Type')[0].innerHTML;
        const creationDate = xmlBlobProps.getElementsByTagName('Creation-Time')[0].innerHTML;
        const sizeInBytes = xmlBlobProps.getElementsByTagName('Content-Length')[0].innerHTML;
        const modificationDate = xmlBlobProps.getElementsByTagName('Last-Modified')[0].innerHTML;

        blobs.push({ name, creationDate, size: sizeInBytes, lastModified: modificationDate, blobType });
    }

    return { blobs, marker: xmlDoc.getElementsByTagName('NextMarker')[0].innerHTML };
};

/**
 * Represents a service for azure container blob operations.
 */
export const getAzureBlobService = () => {
    const buildBlobServiceUrlOptions = (token: string) => {
        const options = luisRequestOptionsBuilder()
            .useContentType('xml')
            .useArmToken(token)
            .skipThrottling()
            .build();

        options.headers[ContainerHeaderName] = '2017-11-09';

        return options;
    };

    /**
     * Uploads a new blob to the givin container or update its content if already existed.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storageservices/put-blob
     *
     * @param param0.blobName The name of the blob to upload.
     * @param param0.blobContent The content of the blob to upload
     * @param param0.storageToken The Azure storage token to use to get the blob containers.
     * @param param0.containerName The name of the selected container.
     * @param param0.storageAccountName The name of the selected storage account.
     */
    const uploadAzureBlobByContainerName = async ({
        blobName,
        blobContent,
        storageToken,
        containerName,
        storageAccountName
    }: {
        blobName: string;
        blobContent: string;
        storageToken: string;
        containerName: string;
        storageAccountName: string;
    }): Promise<void> => {
        const urlOptions = buildBlobServiceUrlOptions(storageToken);

        urlOptions.headers[ContainerHeaderBlobType] = 'BlockBlob';

        const blobUri = getStorageUrl({ storageAccountName, containerName, blobName });

        await httpService.put<string>(blobUri, blobContent, urlOptions);
    };

    /**
     * List the blobs of the givin container.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storageservices/list-blobs
     *
     * @param param0.prefix The prefix string to search for.
     * @param param0.allPortions Retries all blobs in all portions. default: false and it retrieves only the first portion (5K files).
     * @param param0.storageToken The Azure storage token to use to get the blob containers.
     * @param param0.containerName The name of the selected container.
     * @param param0.storageAccountName The name of the selected storage account.
     */
    const getAzureBlobsByContainerName = async ({
        prefix,
        allPortions,
        storageToken,
        containerName,
        storageAccountName
    }: {
        prefix?: string;
        storageToken: string;
        containerName: string;
        allPortions?: boolean;
        storageAccountName: string;
    }): Promise<AzureBlobSwagger[]> => {
        const urlOptions = buildBlobServiceUrlOptions(storageToken);

        urlOptions.headers[ContainerHeaderBlobType] = 'BlockBlob';

        let blobUri = getStorageUrl({
            storageAccountName,
            containerName,
            query: 'restype=container&comp=list'
        });

        if (prefix) {
            blobUri = `${blobUri}&prefix=${prefix}`;
        }

        const { data: firstPortionBlobsSwaggers } = await httpService.get<string>(blobUri, urlOptions);

        const { blobs: firstPortionBlobs, marker: nextPortionMarker } = parseBlobXmlResponse(firstPortionBlobsSwaggers);
        const allBlobs = firstPortionBlobs;
        let nextMarker = nextPortionMarker;

        if (!allPortions) {
            return allBlobs;
        }

        while (nextMarker) {
            const nextBlobUri = `${blobUri}&marker=${nextMarker}`;
            const { data: nextPortionBlobsSwaggers } = await httpService.get<string>(nextBlobUri, urlOptions);
            const { blobs, marker } = parseBlobXmlResponse(nextPortionBlobsSwaggers);
            allBlobs.push(...blobs);
            nextMarker = marker;
        }

        return allBlobs;
    };

    /**
     * List the blobs of the given container in a batch manner.
     * If a marker is provided, the method gets the batch associated with the current marker
     * else it gets the first batch in the container.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storageservices/list-blobs
     *
     * @param param0.storageAccountToken The Azure storage token to use to get the blob containers.
     * @param param0.storageAccountName The name of the selected storage account.
     * @param param0.containerName The name of the selected container.
     * @param param0.marker the marker for the first portion to retrieve (5K files).
     */
    const getAzureBlobsByContainerNameInBatches = async ({
        marker,
        storageToken,
        containerName,
        storageAccountName
    }: {
        marker?: string;
        storageToken: string;
        containerName: string;
        storageAccountName: string;
    }): Promise<{ nextMarker: string; blobs: AzureBlobSwagger[] }> => {
        const urlOptions = buildBlobServiceUrlOptions(storageToken);
        urlOptions.headers[ContainerHeaderBlobType] = 'BlockBlob';

        let blobUri = getStorageUrl({
            storageAccountName,
            containerName,
            query: 'restype=container&comp=list'
        });

        if (marker) {
            blobUri = `${blobUri}&marker=${marker}`;
        }

        const { data: portionBlobSwaggers } = await httpService.get<string>(blobUri, urlOptions);
        const { blobs, marker: nextPortionMarker } = parseBlobXmlResponse(portionBlobSwaggers);

        return {
            blobs,
            nextMarker: nextPortionMarker
        };
    };

    /**
     * Gets the blob content of the given blob name and blob container name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storageservices/get-blob
     *
     * @param param0.blobName The name of the selected blob.
     * @param param0.storageToken The Azure storage token to use to get the blob containers.
     * @param param0.containerName The name of the selected container.
     * @param param0.blobUri [Optional] A ready blob URI to be called directly.
     * @param param0.storageAccountName The name of the selected storage account.
     */
    const getAzureBlobContent = async <T = string>({
        blobName,
        storageToken,
        containerName,
        blobUri = null,
        storageAccountName
    }: {
        blobUri?: string;
        blobName: string;
        storageToken: string;
        containerName: string;
        storageAccountName: string;
    }): Promise<T> => {
        const urlOptions = buildBlobServiceUrlOptions(storageToken);

        urlOptions.headers[ContainerHeaderBlobType] = 'BlockBlob';

        const blobUrl =
            blobUri ??
            getStorageUrl({
                storageAccountName,
                containerName,
                blobName
            });

        const { data: blobContent } = await httpService.get<T>(blobUrl, urlOptions);

        return blobContent;
    };

    return {
        getAzureBlobContent,
        getAzureBlobsByContainerName,
        uploadAzureBlobByContainerName,
        getAzureBlobsByContainerNameInBatches
    };
};
